import React from 'react';
import {
  FormSection,
  FormGrid,
  FormField,
  Input,
  Select,
  Textarea,
  Checkbox,
  TimeInput,
  NumberInput,
  CurrencyInput
} from '../common/Form';
import {
  MATCH_FORMAT_DOUBLES,
  MATCH_FORMAT_SINGLES,
  doublesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionOptions,
  singlesGameTypeRestrictionSelectValue,
} from '../../utils/competitiveMatchFormat';
import {
  DEFAULT_SCORING,
  getScoringRulesSummary,
  normalizeScoringConfig,
} from '../../utils/gameScoreRules';
import {
  DEFAULT_SESSION_START_TIME,
  DEFAULT_SESSION_END_TIME,
  DEFAULT_SESSION_DURATION_MINUTES,
  getDefaultEndTime,
  resolveEndTimeForStartChange,
  getEndTimeMin,
  timeStringToMinutes,
} from '../../utils/sessionTimeUtils';
import { isPegBoardSession, isSelectableSessionType } from '../../utils/sessionTypeUtils';
import { SIGNUP_SELECTION, isHostSelectSignup } from '../../utils/signupSelection';
import {
  PEG_BOARD_QUEUE_RETURN_OPTIONS,
  PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  PEG_BOARD_SELECTION_WINDOW_DEFAULT,
  PEG_BOARD_SELECTION_WINDOW_OPTIONS,
  clampPegBoardSelectionWindow,
  normalizePegBoardQueueReturn,
} from '../../utils/pegBoard/constants';
import SessionSeriesField from './SessionSeriesField';
import { seriesIdOrNull } from '../../utils/sessionSeries';

export {
  DEFAULT_SESSION_START_TIME,
  DEFAULT_SESSION_END_TIME,
  DEFAULT_SESSION_DURATION_MINUTES,
  getDefaultEndTime,
  resolveEndTimeForStartChange,
  getEndTimeMin,
  timeStringToMinutes,
};

/**
 * Default shape for session configuration (shared by one-off session, template, and block booking).
 * Consumers can pass a superset (e.g. + session_date, blockName) but this component only reads/updates these keys.
 */
export const defaultSessionConfig = {
  session_type_id: '',
  name: '',
  description: '',
  start_time: DEFAULT_SESSION_START_TIME,
  end_time: DEFAULT_SESSION_END_TIME,
  max_players: '16',
  number_of_courts: '1',
  min_elo_level: '',
  location: '',
  currency_code: 'GBP',
  price_per_player: '',
  court_booking_cost: '',
  signup_deadline_hours: '',
  waitlist_enabled: true,
  waitlist_promotion_type: 'first_come_first_served',
  signup_selection: SIGNUP_SELECTION.FIRST_COME,
  is_public: false,
  game_type_restriction: 'balanced_only',
  enable_player_categorisation: false,
  competitive_match_format: MATCH_FORMAT_DOUBLES,
  points_target: DEFAULT_SCORING.pointsTarget,
  deuce_mode: DEFAULT_SCORING.deuceMode,
  peg_board_selection_window: PEG_BOARD_SELECTION_WINDOW_DEFAULT,
  peg_board_queue_return: PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  session_series_id: '',
};

/**
 * Maps a club_session_setup template row to session config shape for pre-fill.
 */
export function templateToSessionConfig(template) {
  if (!template) return null;
  const startTime = template.start_time ? template.start_time.substring(0, 5) : DEFAULT_SESSION_START_TIME;
  return {
    session_type_id: template.session_type_id || '',
    name: template.session_name || '',
    description: template.description || '',
    start_time: startTime,
    end_time: template.end_time ? template.end_time.substring(0, 5) : getDefaultEndTime(startTime),
    max_players: template.max_players != null ? String(template.max_players) : (template.ideal_player_count != null ? String(template.ideal_player_count) : '16'),
    number_of_courts: template.number_of_courts != null ? String(template.number_of_courts) : '1',
    min_elo_level: template.min_elo_level != null ? String(template.min_elo_level) : '',
    location: template.location || '',
    currency_code: template.currency_code || 'GBP',
    price_per_player: template.price_per_player != null ? String(template.price_per_player) : '',
    court_booking_cost: template.court_booking_cost != null ? String(template.court_booking_cost) : '',
    signup_deadline_hours: template.signup_deadline_hours != null ? String(template.signup_deadline_hours) : '',
    waitlist_enabled: template.waitlist_enabled !== false || isHostSelectSignup(template.signup_selection),
    waitlist_promotion_type: template.waitlist_promotion_type || 'first_come_first_served',
    signup_selection: isHostSelectSignup(template.signup_selection)
      ? SIGNUP_SELECTION.HOST_SELECT
      : SIGNUP_SELECTION.FIRST_COME,
    is_public: template.is_public || false,
    game_type_restriction: template.game_type_restriction || 'balanced_only',
    enable_player_categorisation: template.enable_player_categorisation || false,
    competitive_match_format: template.competitive_match_format || MATCH_FORMAT_DOUBLES,
    points_target: template.points_target ?? DEFAULT_SCORING.pointsTarget,
    deuce_mode: template.deuce_mode ?? DEFAULT_SCORING.deuceMode,
    peg_board_selection_window: clampPegBoardSelectionWindow(template.peg_board_selection_window),
    peg_board_queue_return: normalizePegBoardQueueReturn(template.peg_board_queue_return),
    session_series_id: template.session_series_id || '',
  };
}

/**
 * SessionConfigFields - Shared controlled form for session configuration.
 * Used by SessionTemplateForm (one-off/template) and BlockBookingModal (block booking).
 * Does not include: session date, day of week, block metadata, recurrence, or submit actions.
 */
const SessionConfigFields = ({
  value,
  onChange,
  sessionTypes = [],
  templates = null,
  selectedTemplateId = '',
  onTemplateSelect,
  fieldErrors = {},
  isModal = false,
  showSessionTypeDescription = true,
  showSessionStatus = false,
  showMakeActive = false,
  nameLabel = 'Session Name',
  filterSessionTypes = null,
  sessionDateProps = null,
  dayOfWeekProps = null,
  requirePlanningFields = true,
  collapsibleSections = false,
  clubId = null,
}) => {
  const config = { ...defaultSessionConfig, ...value };
  const sectionCollapse = (open = false) =>
    collapsibleSections ? { collapsible: true, defaultOpen: open } : {};

  const update = (partial) => {
    onChange({ ...config, ...partial });
  };

  const handleInputChange = (e) => {
    const { name, value: v, type, checked } = e.target;
    update({ [name]: type === 'checkbox' ? checked : (type === 'radio' ? v === 'true' : v) });
  };

  const filteredSessionTypes = (filterSessionTypes
    ? sessionTypes.filter(filterSessionTypes)
    : sessionTypes
  ).filter(isSelectableSessionType);
  const sessionTypeOptions = filteredSessionTypes.map((t) => ({ value: t.id, label: t.name }));
  const selectedSessionType = sessionTypes.find((t) => t.id === config.session_type_id);
  const isCompetitiveSession = selectedSessionType?.name === 'Competitive Session';
  const isPegBoard = isPegBoardSession(selectedSessionType);
  const scoringSummary = getScoringRulesSummary(normalizeScoringConfig(config));
  const scoringLocked = ['ongoing', 'completed', 'archived', 'cancelled'].includes(
    config.session_status,
  );
  const hostSelectSignup = isHostSelectSignup(config.signup_selection);

  return (
    <div className="space-y-6">
      {/* Optional template selection (e.g. for block booking) */}
      {templates && templates.length > 0 && (
        <FormSection title="Use a template (optional)" {...sectionCollapse()}>
          <FormField label="Template" error={fieldErrors.template}>
            <Select
              name="template"
              value={selectedTemplateId || ''}
              onChange={(e) => {
                const id = e.target.value;
                const template = templates.find((t) => t.id === id);
                if (onTemplateSelect) {
                  onTemplateSelect(template);
                } else if (template) {
                  update(templateToSessionConfig(template));
                }
              }}
              placeholder="None - fill in manually"
              options={templates.map((t) => ({ value: t.id, label: t.session_name }))}
            />
          </FormField>
        </FormSection>
      )}

      <FormSection title="Basic Information" description="Core details about your session" {...sectionCollapse(true)}>
        <FormGrid columns={2}>
          <FormField label="Session Type" required error={fieldErrors.session_type_id}>
            <Select
              value={config.session_type_id}
              onChange={(e) => {
                const nextId = e.target.value;
                const nextType = sessionTypes.find((t) => t.id === nextId);
                update({
                  session_type_id: nextId,
                  ...(isPegBoardSession(nextType)
                    ? { competitive_match_format: MATCH_FORMAT_DOUBLES }
                    : {}),
                });
              }}
              name="session_type_id"
              error={fieldErrors.session_type_id}
              options={sessionTypeOptions}
            />
            {showSessionTypeDescription && config.session_type_id && !isModal && (
              <div className="mt-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm">
                <p className="text-sm text-blue-800">
                  <strong>Description:</strong>{' '}
                  {selectedSessionType?.description || 'No description available'}
                </p>
              </div>
            )}
          </FormField>
          <FormField label={nameLabel} required error={fieldErrors.name}>
            <Input
              type="text"
              name="name"
              value={config.name}
              onChange={handleInputChange}
              placeholder="e.g., Monday Night Mixed"
              error={fieldErrors.name}
            />
          </FormField>
        </FormGrid>
        {clubId ? (
          <SessionSeriesField
            clubId={clubId}
            value={config.session_series_id || ''}
            onChange={(nextId) => update({ session_series_id: seriesIdOrNull(nextId) || '' })}
            error={fieldErrors.session_series_id}
          />
        ) : null}
        <FormField label="Description">
          <Textarea
            name="description"
            value={config.description}
            onChange={handleInputChange}
            placeholder={isModal ? 'Brief description of the session...' : 'Optional description of the session'}
            rows={isModal ? 3 : 2}
          />
        </FormField>
      </FormSection>

      <FormSection
        title="Schedule"
        description={
          sessionDateProps || dayOfWeekProps
            ? 'Date and time for the session'
            : 'Session time'
        }
        {...sectionCollapse()}
      >
        <FormGrid columns={3}>
          {dayOfWeekProps && (
            <FormField label="Day of Week" required error={dayOfWeekProps.error}>
              <Select
                name="day_of_week"
                value={dayOfWeekProps.value}
                onChange={dayOfWeekProps.onChange}
                error={dayOfWeekProps.error}
                options={dayOfWeekProps.options}
              />
            </FormField>
          )}
          {sessionDateProps && (
            <FormField label="Session Date" required error={sessionDateProps.error}>
              <Input
                type="date"
                name="session_date"
                value={sessionDateProps.value}
                onChange={sessionDateProps.onChange}
                min={sessionDateProps.min}
                error={sessionDateProps.error}
              />
            </FormField>
          )}
          <FormField label="Start Time" required={requirePlanningFields} error={fieldErrors.start_time}>
            <TimeInput
              label=""
              name="start_time"
              value={config.start_time}
              onChange={(val) => update({
                start_time: val,
                end_time: requirePlanningFields
                  ? resolveEndTimeForStartChange(val, config.end_time)
                  : (config.end_time || ''),
              })}
              error={fieldErrors.start_time}
            />
          </FormField>
          <FormField label="End Time" required={requirePlanningFields} error={fieldErrors.end_time}>
            <TimeInput
              label=""
              name="end_time"
              value={config.end_time}
              onChange={(val) => update({ end_time: val })}
              min={getEndTimeMin(config.start_time)}
              error={fieldErrors.end_time}
            />
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title="Session Details" description="Session capacity and venue details" {...sectionCollapse()}>
        <FormGrid columns={3}>
          <NumberInput
            label={isModal ? 'Max Players' : 'Maximum Players'}
            name="max_players"
            value={config.max_players}
            onChange={handleInputChange}
            placeholder={requirePlanningFields && isModal ? '16' : 'No limit'}
            allowEmpty
            max={100}
            error={fieldErrors.max_players}
          />
          <NumberInput
            label="Number of Courts"
            name="number_of_courts"
            value={config.number_of_courts}
            onChange={handleInputChange}
            placeholder={requirePlanningFields ? (isModal ? '4' : 'e.g., 4') : 'Set on court layout'}
            allowEmpty
            max={20}
            error={fieldErrors.number_of_courts}
          />
          <NumberInput
            label="Min ELO Level"
            name="min_elo_level"
            value={config.min_elo_level}
            onChange={handleInputChange}
            placeholder={isModal ? '1200' : 'e.g., 1200'}
            min={0}
          />
        </FormGrid>
        {hostSelectSignup ? (
          <p className="text-sm text-text-secondary">
            Max players is shown to players as the intended size. You can confirm more if you need to.
          </p>
        ) : null}
        <FormField label="Location">
          <Input
            type="text"
            name="location"
            value={config.location}
            onChange={handleInputChange}
            placeholder={isModal ? 'e.g., Community Center' : 'e.g., Main Hall, Court 1-4'}
          />
        </FormField>
      </FormSection>

      {isCompetitiveSession && (
        <FormSection title="Matchmaking" description="Control how players are matched and grouped" {...sectionCollapse()}>
          <FormField
            label="Match format"
            helperText="Singles uses one player per side (1v1). Doubles is the default (two per side)."
          >
            <Select
              name="competitive_match_format"
              value={config.competitive_match_format || MATCH_FORMAT_DOUBLES}
              onChange={(e) => {
                const next = e.target.value;
                update({
                  competitive_match_format: next,
                  ...(next === MATCH_FORMAT_SINGLES ? { enable_player_categorisation: false } : {}),
                });
              }}
              options={[
                { value: MATCH_FORMAT_DOUBLES, label: 'Doubles (two players per side)' },
                { value: MATCH_FORMAT_SINGLES, label: 'Singles (1v1)' },
              ]}
            />
          </FormField>
          <FormField
            label="Game Type Settings"
            helperText={
              (config.competitive_match_format || MATCH_FORMAT_DOUBLES) === MATCH_FORMAT_SINGLES
                ? 'All types allows same-gender and cross-gender 1v1. Levels singles only is male vs male or female vs female.'
                : 'Control which game types are allowed during matchmaking'
            }
          >
            <Select
              name="game_type_restriction"
              value={
                (config.competitive_match_format || MATCH_FORMAT_DOUBLES) === MATCH_FORMAT_SINGLES
                  ? singlesGameTypeRestrictionSelectValue(config.game_type_restriction)
                  : config.game_type_restriction
              }
              onChange={handleInputChange}
              options={
                (config.competitive_match_format || MATCH_FORMAT_DOUBLES) === MATCH_FORMAT_SINGLES
                  ? singlesGameTypeRestrictionOptions
                  : doublesGameTypeRestrictionOptions
              }
            />
          </FormField>
          <FormField
            label="Enable Player Categorisation"
            helperText={
              (config.competitive_match_format || MATCH_FORMAT_DOUBLES) === MATCH_FORMAT_SINGLES
                ? 'Not used in singles sessions (tier matchmaking is for doubles).'
                : 'Group players into skill tiers (A, B, C, D) based on ELO to prevent large skill gaps in matchmaking'
            }
          >
            <Checkbox
              name="enable_player_categorisation"
              checked={config.enable_player_categorisation}
              onChange={handleInputChange}
              disabled={(config.competitive_match_format || MATCH_FORMAT_DOUBLES) === MATCH_FORMAT_SINGLES}
            />
          </FormField>
        </FormSection>
      )}

      {isPegBoard && (
        <FormSection
          title="Peg Board"
          description="The player at the front of the queue picks the next doubles game"
          {...sectionCollapse()}
        >
          <FormField
            label="Picker can choose from"
            helperText="Includes the picker. Must be at least 4 for doubles."
            error={fieldErrors.peg_board_selection_window}
          >
            <Select
              name="peg_board_selection_window"
              value={String(clampPegBoardSelectionWindow(config.peg_board_selection_window))}
              onChange={(e) => update({ peg_board_selection_window: parseInt(e.target.value, 10) })}
              options={PEG_BOARD_SELECTION_WINDOW_OPTIONS}
            />
          </FormField>
          <FormField
            label="Queue return order"
            helperText="How the four players go back onto the queue after a score is entered"
            error={fieldErrors.peg_board_queue_return}
          >
            <Select
              name="peg_board_queue_return"
              value={normalizePegBoardQueueReturn(config.peg_board_queue_return)}
              onChange={handleInputChange}
              options={PEG_BOARD_QUEUE_RETURN_OPTIONS}
            />
          </FormField>
        </FormSection>
      )}

      <FormSection
        title="Game scoring"
        description="Points target and deuce rules for score entry during the session"
        {...sectionCollapse()}
      >
        <FormGrid columns={2}>
          <FormField
            label="Points to win"
            helperText={scoringLocked ? 'Locked while session is live or finished' : null}
          >
            <Select
              name="points_target"
              value={String(config.points_target ?? DEFAULT_SCORING.pointsTarget)}
              onChange={(e) => update({ points_target: parseInt(e.target.value, 10) })}
              disabled={scoringLocked}
              options={[
                { value: '21', label: '21 points (current default)' },
                { value: '15', label: '15 points (BWF 3×15)' },
              ]}
            />
          </FormField>
          <FormField label="Deuce rules">
            <Select
              name="deuce_mode"
              value={config.deuce_mode || DEFAULT_SCORING.deuceMode}
              onChange={handleInputChange}
              disabled={scoringLocked}
              options={[
                { value: 'standard', label: 'Standard (deuce + cap)' },
                { value: 'no_deuce', label: 'No deuce (first to target wins)' },
              ]}
            />
          </FormField>
        </FormGrid>
        <p className="text-sm text-text-secondary">{scoringSummary}</p>
      </FormSection>

      <FormSection title="Capacity & Waitlist" description="Player capacity and waitlist management" {...sectionCollapse()}>
        <FormField
          label="Who fills confirmed spots"
          helperText={
            hostSelectSignup
              ? 'Everyone who signs up, including people you add or paste, goes onto the list. You choose who is confirmed.'
              : 'Confirmed spots fill in signup order. Extra people go to the waitlist.'
          }
        >
          <Select
            name="signup_selection"
            value={hostSelectSignup ? SIGNUP_SELECTION.HOST_SELECT : SIGNUP_SELECTION.FIRST_COME}
            onChange={(e) => {
              update({
                signup_selection: e.target.value,
                waitlist_enabled: true,
              });
            }}
            options={[
              { value: SIGNUP_SELECTION.FIRST_COME, label: 'First come, first served' },
              { value: SIGNUP_SELECTION.HOST_SELECT, label: 'Host selects from the list' },
            ]}
          />
        </FormField>
        <FormGrid columns={2}>
          <FormField label="Signup Deadline (hours before session)" helperText="Leave empty for no deadline">
            <NumberInput
              name="signup_deadline_hours"
              value={config.signup_deadline_hours}
              onChange={handleInputChange}
              placeholder="e.g., 12 (or leave empty for no deadline)"
              min={0}
              max={168}
            />
          </FormField>
          {!hostSelectSignup ? (
            <FormField label="Waitlist Promotion Type">
              <Select
                name="waitlist_promotion_type"
                value={config.waitlist_promotion_type}
                onChange={handleInputChange}
                options={[
                  { value: 'first_come_first_served', label: 'First come, first served (automatic)' },
                  { value: 'manual_approval', label: 'Manual approval' }
                ]}
              />
            </FormField>
          ) : null}
        </FormGrid>
      </FormSection>

      <FormSection title="Pricing (optional)" description="Cost structure for the session" {...sectionCollapse()}>
        <div className="space-y-6">
          <FormField label="Currency">
            <Select
              name="currency_code"
              value={config.currency_code}
              onChange={handleInputChange}
              options={[
                { value: 'GBP', label: '£ British Pound (GBP)' },
                { value: 'USD', label: '$ US Dollar (USD)' },
                { value: 'EUR', label: '€ Euro (EUR)' },
                { value: 'CAD', label: 'C$ Canadian Dollar (CAD)' },
                { value: 'AUD', label: 'A$ Australian Dollar (AUD)' }
              ]}
            />
          </FormField>
          <FormGrid columns={2}>
            <FormField label="Price per Player">
              <CurrencyInput
                name="price_per_player"
                value={config.price_per_player}
                onChange={handleInputChange}
                placeholder="e.g., 15.00"
                currency={config.currency_code}
              />
            </FormField>
            <FormField label="Court Booking Cost">
              <CurrencyInput
                name="court_booking_cost"
                value={config.court_booking_cost}
                onChange={handleInputChange}
                placeholder="e.g., 25.00"
                currency={config.currency_code}
              />
            </FormField>
          </FormGrid>
        </div>
      </FormSection>

      <FormSection title="Settings" description="Session behavior and status" {...sectionCollapse()}>
        {showSessionStatus && (
          <FormField label="Session Status">
            <Select
              name="session_status"
              value={config.session_status ?? 'upcoming'}
              onChange={(e) => update({ session_status: e.target.value })}
              options={[
                { value: 'draft', label: 'Draft - Hidden from users' },
                { value: 'upcoming', label: 'Upcoming - Visible to users' }
              ]}
            />
          </FormField>
        )}
        {showMakeActive && (
          <FormField label="Make Active" helperText="Available for booking">
            <Checkbox
              name="is_active"
              checked={config.is_active !== false}
              onChange={handleInputChange}
            />
          </FormField>
        )}
        <FormField label="Session Visibility">
          <Select
            name="is_public"
            value={config.is_public ? 'true' : 'false'}
            onChange={handleInputChange}
            options={[
              { value: 'false', label: 'Private - Club Members Only' },
              { value: 'true', label: 'Public - All Users Can Join' }
            ]}
          />
        </FormField>
      </FormSection>
    </div>
  );
};

export default SessionConfigFields;
